/**
 * Configuración para gráfico combinado (barras + línea) estilo "Curva de Bradley".
 * Muestra la calificación promedio real por pilar y una curva ideal descendente.
 */

export default function lineAverage(data, ratingDefinitions = []) {
    if (!data || !data.by_pillar) {
        return {};
    }

    const ratingColumns = normalizeRatings(ratingDefinitions);

    // Preparar datos de pilares
    const pillars = data.by_pillar.map((pillar) => {
        const ratings = pillar.ratings || {};
        const { average } = calculateAverageFromRatings(ratings, ratingColumns);
        const baseColor = pillar.color || '#3b82f6';

        return {
            name: pillar.name,
            average,
            color: baseColor,
            barColor: lightenColor(baseColor, 0.25),
            ratingCounts: buildRatingCounts(ratings, ratingColumns)
        };
    });

    if (!pillars.length) {
        return {};
    }

    const categories = pillars.map(p => p.name);
    const actualValues = pillars.map(p => Number(p.average));

    const maxValue = ratingColumns.reduce((max, rating) => {
        return Math.max(max, typeof rating.value === 'number' ? rating.value : max);
    }, 0) || 5;

    const minValue = ratingColumns.reduce((min, rating) => {
        if (typeof rating.value === 'number') {
            return Math.min(min, rating.value);
        }
        return min;
    }, maxValue);

    return {
        /* title: {
            text: 'Curva de Bradley – Real vs Ideal',
            left: 'center',
            top: 15,
            textStyle: {
                fontSize: 16,
                fontWeight: 'bold',
                color: '#1B212E'
            }
        }, */
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow'
            },
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            borderColor: '#e2e8f0',
            borderWidth: 1,
            padding: 12,
            textStyle: {
                color: '#1B212E',
                fontSize: 13
            },
            formatter: (params = []) => {
                const firstParam = params && params.length ? params[0] : null;
                if (!firstParam || typeof firstParam.dataIndex === 'undefined') {
                    return '';
                }

                const pillarData = pillars[firstParam.dataIndex];
                const lines = [
                    `<strong>${pillarData.name}</strong>`,
                    `Calificación promedio: <strong>${Number(firstParam.value).toFixed(2)}</strong>`
                ];

                if (pillarData.ratingCounts && pillarData.ratingCounts.length) {
                    const rows = pillarData.ratingCounts.map((entry) => {
                        return `
                            <span style="display:flex;align-items:center;gap:4px;margin-bottom:2px;">
                                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${entry.color || '#9ca3af'}"></span>
                                ${entry.name}: <strong>${entry.count}</strong>
                            </span>
                        `;
                    }).join('');
                    lines.push(`<div style="margin-top:4px;">${rows}</div>`);
                }

                return lines.join('<br/>');
            }
        },
       /*  legend: {
            data: ['Calificación promedio'],
            top: 0,
            textStyle: {
                color: '#374151'
            }
        }, */
        grid: {
            left: '6%',
            right: '4%',
            bottom: '10%',
            top: '15%',
            containLabel: true
        },
        xAxis: {
            type: 'category',
            data: categories,
            axisLine: {
                lineStyle: {
                    color: '#cbd5e0'
                }
            },
            axisLabel: {
                color: '#64748b',
                fontSize: 11,
                interval: 0,
                margin: 10,
                formatter: formatCategoryLabel
            },
            axisTick: { show: false }
        },
        yAxis: {
            type: 'value',
            min: Math.min(0, minValue),
            max: Math.max(maxValue, Math.max.apply(Math, actualValues)),
            name: 'Nivel de madurez',
            nameLocation: 'middle',
            nameGap: 45,
            nameTextStyle: {
                color: '#64748b',
                fontSize: 11,
                fontWeight: 600
            },
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
                color: '#64748b',
                fontSize: 11
            },
            splitLine: {
                lineStyle: {
                    color: '#f1f5f9',
                    type: 'dashed'
                }
            }
        },
        series: [
            {
                name: 'Calificación promedio',
                type: 'bar',
                barWidth: 40,
                data: pillars.map((pillar) => ({
                    value: Number(pillar.average),
                    itemStyle: {
                        color: pillar.barColor,
                        borderRadius: [8, 8, 0, 0]
                    }
                }))
            }
        ]
    };
}

function normalizeRatings(ratingDefinitions) {
    if (ratingDefinitions && ratingDefinitions.length) {
        return ratingDefinitions.map((rating, index) => ({
            ...rating,
            lookupKey: rating.id,
            fallbackKey: `rating_${index}`
        }));
    }

    const defaults = [
        { id: 'legacy-0', name: 'No aplica', value: 0, color: '#6c757d' },
        { id: 'legacy-1', name: 'Muy bajo', value: 1, color: '#dc3545' },
        { id: 'legacy-2', name: 'Bajo', value: 2, color: '#fd7e14' },
        { id: 'legacy-3', name: 'Medio', value: 3, color: '#ffc107' },
        { id: 'legacy-4', name: 'Alto', value: 4, color: '#17a2b8' },
        { id: 'legacy-5', name: 'Muy alto', value: 5, color: '#28a745' }
    ];

    return defaults.map((rating, index) => ({
        ...rating,
        lookupKey: `rating_${index}`,
        fallbackKey: `rating_${index}`
    }));
}

function calculateAverageFromRatings(ratings, ratingColumns) {
    if (!ratings) {
        return { average: 0, total: 0 };
    }

    const stats = ratings.ratings || null;
    let total = ratings.total || 0;
    let sum = 0;

    ratingColumns.forEach((rating, index) => {
        const entry = getRatingEntry(ratings, rating, index, stats);
        if (entry) {
            sum += entry.count * (typeof rating.value === 'number' ? rating.value : index);
            if (!ratings.total) {
                total += entry.count;
            }
        }
    });

    return {
        average: total > 0 ? Number((sum / total).toFixed(2)) : 0,
        total
    };
}

function getRatingEntry(ratings, rating, index, stats) {
    if (stats && Object.prototype.hasOwnProperty.call(stats, rating.lookupKey)) {
        return stats[rating.lookupKey];
    }

    const legacyKey = rating.fallbackKey || rating.lookupKey || `rating_${index}`;
    if (ratings[legacyKey]) {
        return ratings[legacyKey];
    }

    return null;
}

function formatCategoryLabel(value) {
    if (!value) return '';
    const maxCharsPerLine = 18;
    const words = value.split(' ');
    let lines = [];
    let currentLine = '';

    words.forEach(word => {
        if ((currentLine + word).length <= maxCharsPerLine) {
            currentLine += (currentLine ? ' ' : '') + word;
        } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
        }
    });

    if (currentLine) lines.push(currentLine);

    if (lines.length > 3) {
        lines = lines.slice(0, 3);
        lines[2] = lines[2].substring(0, maxCharsPerLine - 3) + '...';
    }

    return lines.join('\n');
}

function buildRatingCounts(ratings, ratingColumns) {
    const stats = ratings && ratings.ratings ? ratings.ratings : null;
    return ratingColumns.map((rating, index) => {
        const entry = getRatingEntry(ratings, rating, index, stats);
        return {
            name: rating.name,
            color: rating.color,
            count: entry && typeof entry.count === 'number' ? entry.count : 0
        };
    });
}

function lightenColor(hex, amount) {
    if (!hex) {
        return '#d1d5db';
    }

    let normalized = hex.replace('#', '');
    if (normalized.length === 3) {
        normalized = normalized.split('').map(char => char + char).join('');
    }

    const numeric = parseInt(normalized, 16);
    const r = (numeric >> 16) & 0xff;
    const g = (numeric >> 8) & 0xff;
    const b = numeric & 0xff;

    const factor = typeof amount === 'number' ? amount : 0.25;
    const adjust = (channel) => {
        const value = Math.round(channel + (255 - channel) * factor);
        return Math.max(0, Math.min(255, value));
    };

    const toHex = (channel) => channel.toString(16).padStart(2, '0');

    return `#${toHex(adjust(r))}${toHex(adjust(g))}${toHex(adjust(b))}`;
}
